import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import 'moment/locale/tr';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { apiUrl, fetchJson } from '../../apiBase';

const DAY_MAP = {
  'Pazartesi': 1,
  'Salı': 2,
  'Çarşamba': 3,
  'Perşembe': 4,
  'Cuma': 5,
  'Cumartesi': 6,
  'Pazar': 0,
};

function buildEventsFromSchedule(schedule, baseDate = new Date()) {
  const startOfWeek = moment(baseDate).startOf('isoWeek');
  const events = [];
  const title = (subject, cls) => `${String(subject || '').trim()} • ${String(cls || '').trim()}`.trim();
  Object.entries(schedule || {}).forEach(([dayName, lessons]) => {
    const dow = DAY_MAP[dayName] ?? 1;
    const base = startOfWeek.clone().add(dow - 1, 'days');
    (lessons || []).forEach((l, idx) => {
      const [sh, sm] = String(l.start || '00:00').split(':').map(Number);
      const [eh, em] = String(l.end || '00:00').split(':').map(Number);
      const start = base.clone().hour(sh).minute(sm).toDate();
      const end = base.clone().hour(eh).minute(em).toDate();
      events.push({
        id: `${dayName}-${idx}`,
        title: title(l.subject, l.class_name),
        start,
        end,
      });
    });
  });
  return events;
}

export default function TeacherSchedule({ schoolId, onError, user }) {
  const [teachers, setTeachers] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [schedule, setSchedule] = useState({});
  const [date, setDate] = useState(() => new Date());

  // Role info
  const isTeacher = !!(user && user.role === 'teacher');
  const teacherFullName = isTeacher ? `${user?.name || ''} ${user?.surname || ''}`.trim() : '';
  const teacherUserId = isTeacher ? String(user?.id || user?.teacher_id || user?.teacherId || '') : '';

  // Auth headers
  const token = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('adminUser') || '{}')?.token; } catch { return undefined; }
  }, []);
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // 24-hour TR locale
  useEffect(() => { try { moment.updateLocale('tr', { week: { dow: 1, doy: 4 } }); moment.locale('tr'); } catch {} }, []);
  const localizer = useMemo(() => momentLocalizer(moment), []);
  const events = useMemo(() => buildEventsFromSchedule(schedule, date), [schedule, date]);

  const loadTeachers = useCallback(async () => {
    if (!schoolId || isTeacher) return;
    try {
  const res = await fetchJson(apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}`), { headers: authHeaders });
      if (!res.ok) throw new Error('teachers');
      const body = await res.json();
      const list = Array.isArray(body) ? body : (body?.teachers || []);
      setTeachers(list || []);
      if (list && list.length && !selectedId) setSelectedId(String(list[0]?.id || ''));
    } catch (e) { onError && onError('Öğretmenler yüklenemedi'); }
  }, [schoolId, onError, selectedId, isTeacher, authHeaders]);

  const loadSchedule = useCallback(async (teacherIdOrName) => {
    if (!schoolId) { setSchedule({}); return; }
    try {
      const query = isTeacher
        ? (teacherUserId
            ? `teacher_id=${encodeURIComponent(teacherUserId)}`
            : `teacher_name=${encodeURIComponent(teacherFullName)}`)
        : `teacher_id=${encodeURIComponent(teacherIdOrName || '')}`;
      if (!isTeacher && !teacherIdOrName) { setSchedule({}); return; }
  const res = await fetchJson(apiUrl(`/admin/schedules/${encodeURIComponent(schoolId)}/teacher?${query}`), { headers: authHeaders });
      if (!res.ok) { setSchedule({}); return; }
      const body = await res.json();
      setSchedule(body || {});
    } catch (e) { setSchedule({}); }
  }, [schoolId, isTeacher, teacherFullName, teacherUserId, authHeaders]);

  useEffect(() => { loadTeachers(); }, [loadTeachers]);
  useEffect(() => {
    if (isTeacher) {
      loadSchedule(teacherFullName);
    } else if (selectedId) {
      loadSchedule(selectedId);
    }
  }, [isTeacher, teacherFullName, selectedId, loadSchedule]);

  const teacherOptions = useMemo(() => (teachers || []).map((t) => ({
    id: String(t?.id || ''),
    label: `${t?.name || ''} ${t?.surname || ''}`.trim(),
  })).filter((x) => x.id && x.label), [teachers]);
  const valueObj = teacherOptions.find((x) => x.id === selectedId) || null;

  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Öğretmen Ders Programı</Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', sm: 420 } }}>
          {!isTeacher ? (
            <>
              <Autocomplete
                options={teacherOptions}
                value={valueObj}
                onChange={(_, v) => setSelectedId(v?.id || '')}
                disableClearable
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} label="Öğretmen" size="small" placeholder="Öğretmen seçin" />
                )}
              />
              <IconButton aria-label="Yenile" onClick={() => loadSchedule(selectedId)}><RefreshIcon /></IconButton>
            </>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {teacherFullName}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Box sx={{ height: 600 }}>
        <Calendar
          localizer={localizer}
          culture="tr"
          events={events}
          defaultView={Views.WEEK}
          views={[Views.DAY, Views.WEEK]}
          date={date}
          onNavigate={(d) => setDate(d)}
          selectable={false}
          resizable={false}
          toolbar
          formats={{
            eventTimeRangeFormat: ({ start, end }) => `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
            agendaTimeRangeFormat: ({ start, end }) => `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
            selectRangeFormat: ({ start, end }) => `${moment(start).format('DD MMM ddd HH:mm')} - ${moment(end).format('DD MMM ddd HH:mm')}`,
            timeGutterFormat: (d) => moment(d).format('HH:mm'),
          }}
          style={{ minHeight: 500 }}
        />
      </Box>
    </Paper>
  );
}
